import { useMemo, useState } from 'react';
import ExcelJS from 'exceljs';
import { Button, Dropdown, MenuProps } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import * as FileSaver from 'file-saver';
import { handleMenuClick, generateMenuItems } from 'utils/excelUtils';
import { formatInputMoney } from 'utils';
import { toLocalTime } from 'utils';

interface Props {
  fileName: string;
  dataToExport: any[];
  t: (key: string) => string;
  fromDate: string;
  toDate: string;
}

const ExportFocsServicesReport = ({ fileName, dataToExport, t, fromDate, toDate }: Props) => {
  const [isLoadingExport, setIsLoadingExport] = useState(false);

  const exportToCSV = async () => {
    const fileType =
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const fileExtension = '.xlsx';

    try {
      setIsLoadingExport(true);

      const workbook = new ExcelJS.Workbook();
      const sheetName = `Focs Services Report`;
      const sheet = workbook.addWorksheet(sheetName, {
        views: [
          {
            showGridLines: false
          }
        ]
      });

      sheet.mergeCells('A2:G2');
      sheet.getCell('A2').value = 'BÁO CÁO DỊCH VỤ FOC';
      sheet.getCell('A2').font = { size: 11, bold: true };
      sheet.getCell('A2').alignment = { horizontal: 'center' };

      sheet.getCell('A4').value = `Ngày`;
      sheet.getCell('A4').font = { bold: true };
      sheet.getCell('B4').value = fromDate;
      sheet.getCell('C4').value = `Đến`;
      sheet.getCell('C4').font = { bold: true };
      sheet.getCell('D4').value = toDate;

      sheet.getRow(6).values = [
        t('common.bookingData.bookingId'),
        t('common.customer.name'),
        t('common.bookingData.services.service'),
        t('common.bookingData.defaultPrice'),
        t('common.bookingData.services.qty'),
        t('managePage.focsReportPage.focService.calculatePrice'),
        t('managePage.focsReportPage.common.dateOfFoc')
      ];

      sheet.getRow(6).eachCell(cell => {
        cell.border = {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } }
        };
        cell.font = { bold: true };
        cell.alignment = { horizontal: 'center' };
      });

      sheet.columns = [
        { key: 'booking_id', width: 20 },
        { key: 'partner_name', width: 30 },
        { key: 'service_name', width: 25 },
        { key: 'service_default_price', width: 18 },
        { key: 'services_qty', width: 14 },
        { key: 'calculate_price', width: 18 },
        { key: 'date_of_foc', width: 18 }
      ];

      dataToExport.forEach(item => {
        const row = sheet.addRow({
          booking_id: item.booking_id,
          partner_name: item.partner_name,
          service_name: item.service_name,
          service_default_price: item.service_default_price
            ? formatInputMoney(item.service_default_price)
            : '',
          services_qty: item.services_qty,
          calculate_price: item.calculate_price ? formatInputMoney(item.calculate_price) : '',
          date_of_foc: item.date_of_foc ? toLocalTime(item.date_of_foc, 'DD/MM/YYYY') : ''
        });

        row.eachCell({ includeEmpty: true }, cell => {
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          };
          cell.alignment = { horizontal: 'center' };
        });
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const data = new Blob([buffer], { type: fileType });
      FileSaver.saveAs(data, fileName + fileExtension);
    } catch (e) {
      return { status: 'ERROR', error: e };
    } finally {
      setIsLoadingExport(false);
    }
  };

  const handleClickMenu = (menu: any) => {
    handleMenuClick(menu.key, exportToCSV);
  };
  const items: MenuProps['items'] = useMemo(() => generateMenuItems(), []);

  return (
    <Dropdown
      menu={{ items, onClick: handleClickMenu }}
      trigger={['click']}
      overlayClassName="menu-user"
    >
      <Button icon={<DownloadOutlined />} loading={isLoadingExport}>
        {t('common.actions.exportData')}
      </Button>
    </Dropdown>
  );
};

export default ExportFocsServicesReport;
